/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventStepEvent — step 1 of 3.
 *
 * What the event is, and where and when it happens.
 *
 * The admin form opens by asking who is authoring this event and which
 * organization is hosting it. An organizer is never asked: they belong to one,
 * it is resolved from RLS, and it is stated rather than chosen. The picker
 * below appears only in the genuinely ambiguous case of an account attached to
 * more than one organization.
 *
 * `slug` is absent on purpose. `buildEventBatch` derives it from the title.
 */

import React, { useEffect, useMemo, useState } from 'react';
import OrgInput from './OrgInput';
// import OrgTextArea from './OrgTextArea';   // description is rich text now
import OrgRichText from './OrgRichText';
import OrgSelect from './OrgSelect';
import OrgDateTime from './OrgDateTime';
import { OrgChips } from './OrgChoice';
import FileUploadInput from '../../common/FileUploadInput';
import { COVER_FIELD } from '../../athlohive/pendingUploads';
import { DEFAULT_COUNTRY } from '../../athlohive/countries';
import { COVER_HINT, checkCoverImage, readImageDimensions } from '../../athlohive/coverImageGuidance';
import { groupedTimezoneOptions } from '../../athlohive/canonicalTimezones';
import { stateOptions } from '../../athlohive/indianStates';
import { EVENT_VISIBILITY_OPTIONS, EVENT_VISIBILITY_HELP } from '../../athlohive/eventVisibility';
import {
  MAX_EVENT_TITLE_LENGTH, MAX_EVENT_DESCRIPTION_LENGTH, MAX_EVENT_LOCATION_LENGTH,
  MAX_EVENT_PINCODE_LENGTH,
} from '../../athlohive/validateEventForm';

/** The pack's own labels for `event.activity_mode`. */
/* Built once. OrgSelect memoises on the `options` it is handed, and a fresh
   array each render would rebuild its flattened list on every keystroke in the
   form. The list cannot change at runtime, so there is nothing to recompute.

   ONE FLAT LIST, not grouped. The States / Union Territories split was a true
   distinction but not a useful one here: an organizer looking for Delhi wants
   Delhi, and asking them to notice which half of the list it lives in is a
   step that can only slow them down. The distinction still matters for tax,
   where it is drawn from the state itself rather than from this picker. */
const STATE_OPTIONS = stateOptions();

const ACTIVITY_MODES = [
  { value: 'DEFAULT', label: 'Default (single activity)' },
  { value: 'PARALLEL', label: 'Parallel (pick one category)' },
  { value: 'SEQUENTIAL', label: 'Sequential (ordered legs)' },
];

export default function EventStepEvent({
  form, errors, dispatch, organization, pendingUploads = {}, setPendingFile,
  readOnly = false,
}) {
  const set = (field) => (value) => dispatch({ type: 'setField', field, value });

  // The state a row held that could not be matched to the list, carried by the
  // edit form so the picker can say what was there. Cleared the moment a real
  // state is chosen — a note about the old value has no business outliving it.
  const unmatchedState = form.location_state ? '' : (form.location_state_unmatched || '');

  // `groupedTimezoneOptions` names each group `group`; OrgSelect's `groups`
  // prop reads `label`. Renamed here rather than in the timezone module, which
  // has its own callers and its own tests.
  // Advice about the chosen cover, recomputed whenever a new file is picked.
  // Held here rather than in the form because it is not data — it never gets
  // saved, and it must not survive a file being replaced with a better one.
  const [coverAdvice, setCoverAdvice] = useState(null);
  const coverFile = pendingUploads[COVER_FIELD] || null;

  useEffect(() => {
    let current = true;
    if (!coverFile) {
      setCoverAdvice(null);
      return undefined;
    }
    readImageDimensions(coverFile).then((dimensions) => {
      if (current) setCoverAdvice(checkCoverImage(dimensions));
    });
    return () => { current = false; };
  }, [coverFile]);

  const timezoneGroups = useMemo(
    () => groupedTimezoneOptions().map((group) => ({
      label: group.group,
      options: group.options,
    })),
    []
  );


  return (
    // Step 1 has no disclosure controls — every control on it is data entry —
    // so one fieldset covers it. The steps that DO have collapsible sections
    // disable their fields more narrowly.
    <fieldset className="orgf-fieldset" disabled={readOnly}>
      <section className="org-card" style={{ marginBottom: 15 }}>
        <p className="org-lab">What is the event?</p>

        {/*
          Read-only, never a picker. An organizer authors as THEIR OWN
          organization — it is resolved from the signed-in account, not chosen,
          and choosing somebody else's is not a thing they may do. It is shown
          rather than hidden so the event's author is never a mystery, and
          disabled rather than absent so it reads as a fact about the event
          rather than a field somebody forgot to fill in.
        */}
        <div className="orgf-field" style={{ marginTop: 13 }}>
          <label htmlFor="orgf-host">Hosting organization</label>
          <input
            id="orgf-host"
            className="orgf-input"
            type="text"
            disabled
            readOnly
            value={organization.organizationName || ''}
            aria-describedby="orgf-host-help"
          />
          <span className="orgf-help" id="orgf-host-help">
            Taken from your account. Every event you create is authored by this organization.
          </span>
        </div>

        <div className="orgf-grid" style={{ marginTop: 15 }}>
          <OrgInput
            label="Event title" required
            value={form.title} onChange={set('title')} error={errors.title}
            placeholder="Bengaluru Midnight Half Marathon"
            maxLength={MAX_EVENT_TITLE_LENGTH}
          />

          {/* Stacked, not side by side: the cover URL and the rich-text
              description each need the full width to be usable. */}
          <div className="orgf-grid">
            <div className="orgf-field">
              <label htmlFor="orgf-cover">Cover image<span className="orgf-req" aria-hidden="true">*</span></label>
              {/*
                `pendingFile` and `onPendingFile` are what make this field work
                at all. FileUploadInput does not upload on pick — it hands the
                File up and expects the screen to upload it on save. Passing
                only value/onChange, as this did, meant the File was handed to
                nobody: the picker opened, a file was chosen, and nothing
                happened. The URL box is read-only, so there was no way round it
                either, and a cover image is required — which made Publish
                unreachable for every event.
              */}
              <FileUploadInput
                is_public
                value={form.cover_image_url}
                onChange={set('cover_image_url')}
                pendingFile={pendingUploads[COVER_FIELD] || null}
                onPendingFile={(file) => setPendingFile(COVER_FIELD, file)}
                allowedFileTypes={['image']}
                error={errors.cover_image_url}
                inputProps={{ id: 'orgf-cover', name: COVER_FIELD }}
              />
              {/*
                Rendered here rather than passed as FileUploadInput's `hint`.
                That prop styles itself inline — .96em, #545d6f, and a 5px left
                indent — so next to every other field's `.orgf-help` the cover
                guidance came out bigger, darker and nudged out of the column,
                reading as though it belonged to the description box beside it.
              */}
              <span className="orgf-help">{COVER_HINT}</span>
              {/*
                Advisory, never blocking — role="status" rather than an error,
                and the publish path never sees it. A cover image is a
                judgement call the organizer can see the result of, and a hard
                rule here is one that gets worked around with a resized
                screenshot rather than a better photo.
              */}
              {coverAdvice && (
                <span className="orgf-help orgf-warn" role="status">
                  {coverAdvice.message}
                </span>
              )}
            </div>

            {/* <OrgTextArea
              label="Description" required
              value={form.description} onChange={set('description')} error={errors.description}
              placeholder="Tell people what this event is."
              maxLength={MAX_EVENT_DESCRIPTION_LENGTH}
            /> */}
            {/* The one field on this step long enough to need shape. The count
                still measures typed words, so formatting never costs budget. */}
            <OrgRichText
              label="Description" required
              value={form.description} onChange={set('description')} error={errors.description}
              placeholder="Tell people what this event is."
              maxLength={MAX_EVENT_DESCRIPTION_LENGTH}
            />
          </div>

          <OrgInput
            label="Official website" type="url"
            value={form.official_website_url} onChange={set('official_website_url')}
            placeholder="https://"
            help="Optional. Your own page for this event, if you have one."
          />
        </div>
      </section>

      <section className="org-card">
        <p className="org-lab">Where and when</p>

        <div className="orgf-grid" style={{ marginTop: 13 }}>
          {/*
            A plain text box, by decision.

            It used to be a place search that filled State, Country and Pincode
            in from the chosen match. That went because of what it WROTE: the
            column is a city name — `idx_event_location` and the public event
            card both read it that way — and a Nominatim match hands back its
            full display_name, so picking "Kanteerava Stadium" filed the event
            under a 180-character address beginning with a building.

            The cost is stated plainly rather than hidden: the three fields
            below are now typed by hand.
          */}
          <OrgInput
            label="City" required
            value={form.location} onChange={set('location')}
            error={errors.location}
            placeholder="Bengaluru"
            help="The city the event is held in."
            maxLength={MAX_EVENT_LOCATION_LENGTH} showCount
          />

          <div className="orgf-grid orgf-g3">
            <OrgSelect
              label="State" required
              value={form.location_state} onChange={set('location_state')}
              error={errors.location_state}
              options={STATE_OPTIONS}
              placeholder="Choose a state"
              // Filtered in the page, never over the network: all 36 are
              // already here. Typing "kar" or "nad" narrows the list, which
              // beats the built-in type-ahead because that matches only from
              // the START of a name -- no help to somebody who thinks of
              // "Tamil Nadu" as "Nadu".
              searchable
              searchPlaceholder="Type to find a state"
              // Said only when a value could not be matched to the list, so an
              // organizer editing an old event is told what was there instead
              // of finding the field mysteriously empty. The value is NOT kept
              // as an option: an unrecognisable state has no right answer, and
              // offering it back would let it be re-saved.
              help={unmatchedState
                ? `Previously saved as “${unmatchedState}”. Please choose a state.`
                : undefined}
            />
            {/*
              Stated, not chosen. AthloHive takes entries in India only, so the
              country is a fact about every event rather than a decision — a
              250-row picker with one correct answer is a step that can only be
              got wrong. Shown rather than hidden so the address still reads as
              a complete address, and disabled rather than absent so it does not
              look like a field somebody forgot to fill in.
            */}
            <div className="orgf-field">
              <label htmlFor="orgf-country">Country</label>
              <input
                id="orgf-country"
                className="orgf-input"
                type="text"
                disabled
                readOnly
                value={form.location_country || DEFAULT_COUNTRY}
              />
            </div>
            <OrgInput
              label="Pincode"
              value={form.location_pincode}
              // Digits only: anything else is dropped as it is typed or pasted,
              // so "560 001" lands as "560001".
              onChange={(value) => set('location_pincode')(value.replace(/\D/g, ''))}
              error={errors.location_pincode}
              inputMode="numeric"
              maxLength={MAX_EVENT_PINCODE_LENGTH}
            />
          </div>

          <OrgSelect
            label="Timezone" required
            value={form.timezone} onChange={set('timezone')} error={errors.timezone}
            groups={timezoneGroups}
            placeholder="Choose a timezone"
            help="Times below are read against this zone, not your browser's."
          />

          <div className="orgf-grid orgf-g2">
            <OrgDateTime
              label="Starts" required
              value={form.start_datetime} onChange={set('start_datetime')}
              error={errors.start_datetime}
            />
            <OrgDateTime
              label="Ends" required
              value={form.end_datetime} onChange={set('end_datetime')}
              error={errors.end_datetime}
            />
          </div>

          <OrgChips
            label="How do the activities run?" required
            value={form.activity_mode} onChange={set('activity_mode')}
            options={ACTIVITY_MODES} error={errors.activity_mode}
          />

          {/* Narrow: three one-word options do not need the full row. */}
          <div className="orgf-visibility">
            <OrgSelect
              label="Visibility" required
              value={form.visibility} onChange={set('visibility')}
              error={errors.visibility}
              options={EVENT_VISIBILITY_OPTIONS}
              placeholder="Choose who can see it"
              help={EVENT_VISIBILITY_HELP[form.visibility]}
            />
          </div>
        </div>
      </section>
    </fieldset>
  );
}
